import DEFAULT_I18N from "~/i18n"
import { ucfirst } from "~/lib/utils"

import ProfileService from "./ProfileService"

export function makeDefaultState() {
    return {
        currentUser: null,
        editableProfileFields: [],
    }
}

export const getters = {
    languagesAsOptions: (_state, _getters, rootState) => {
        const languagesToSelect = rootState.configs["languagesToSelect"] || []
        if (!DEFAULT_I18N.global.availableLocales.length) return []
        return languagesToSelect.map(lang => {
            const translationKey = lang == "ca" ? "ct" : lang
            return {
                text: ucfirst(
                    DEFAULT_I18N.global.t(
                        `common.language${ucfirst(translationKey)}`
                    )
                ),
                value: lang,
            }
        })
    },

    currentUser: state => {
        return state.currentUser
    },

    currentUserId: state => {
        return state.currentUser?.userId
    },

    itsMe: (_state, getters) => userId => {
        return getters.currentUserId == userId
    },
}

export const mutations = {
    setCurrentUser(state, user) {
        state.currentUser = user
    },

    setEditableFields(state, editableProfileFields) {
        state.editableProfileFields = editableProfileFields
    },

    setUserField(state, { key, value }) {
        const editableProfileFields = state.editableProfileFields
        const fieldIdx = editableProfileFields.findIndex(f => f.prop === key)
        if (fieldIdx === -1) {
            return
        }
        editableProfileFields[fieldIdx].value = value
    },

    clearModule(state) {
        state.editableProfileFields = []
        state.currentUser = null
    },
}

export function makeActions(i18n, service) {
    i18n = i18n || DEFAULT_I18N.global

    return {
        changePassword(
            { state, commit, dispatch },
            { newPassword, repeatNewPassword, currentPassword }
        ) {
            return service
                .updatePassword(newPassword, repeatNewPassword, currentPassword)
                .then(() => {
                    dispatch(
                        "pushNotification",
                        {
                            type: "success",
                            title: ucfirst(
                                i18n.t("common.passwordSuccessfullyUpdated")
                            ),
                        },
                        { root: true }
                    )
                    const loginSteps = {
                        ...(state.currentUser?.loginSteps ?? {}),
                        password: false,
                    }
                    commit("setCurrentUser", {
                        ...state.currentUser,
                        loginSteps,
                    })
                })
                .catch(err => {
                    const { response } = err
                    dispatch(
                        "pushNotification",
                        {
                            title: ucfirst(i18n.t("common.error")),
                            info: response.errors,
                            type: "danger",
                        },
                        { root: true }
                    )
                })
        },

        async changeLanguage({ state, commit, dispatch }, language) {
            const languageToSubmit = language == "ca" ? "ct" : language
            const user = await dispatch("updateProfileAttribute", {
                language: languageToSubmit,
            })
            const diff = await dispatch("loadLocale", user.language, {
                root: true,
            })
            if (diff) {
                dispatch("structure/refreshStructure", undefined, {
                    root: true,
                })
                commit("lists/clearAllFilterAndOrderDescriptions", undefined, {
                    root: true,
                })
                dispatch("fetchEditableFields")
                dispatch("cookies/refresh", undefined, { root: true })
            }
            const loginSteps = {
                ...(state.currentUser?.loginSteps ?? {}),
                language: false,
            }
            commit("setCurrentUser", {
                ...state.currentUser,
                loginSteps,
            })
            return user
        },

        async updateProfile({ commit, dispatch }, user) {
            try {
                const newUser = await service.updateProfile(user)
                commit("setCurrentUser", newUser)

                Object.keys(user).forEach(key => {
                    const value = user[key]
                    commit("setUserField", { key, value })
                })

                return newUser
            } catch (error) {
                dispatch(
                    "pushNotification",
                    {
                        type: "error",
                        title: error.message,
                    },
                    { root: true }
                )
            }
        },

        async updateProfileAttribute({ commit, dispatch }, user) {
            try {
                const newUser = await service.updateProfileAttribute(user)
                commit("setCurrentUser", newUser)

                Object.keys(user).forEach(key => {
                    const value = user[key]
                    commit("setUserField", { key, value })
                })

                return newUser
            } catch (error) {
                dispatch(
                    "pushNotification",
                    {
                        type: "error",
                        title: error.message,
                    },
                    { root: true }
                )
            }
        },

        async updateAvatar({ commit }, { fileHash }) {
            const user = await service.updateAvatar(fileHash)
            commit("setCurrentUser", user)
        },

        async deleteAvatar({ commit, dispatch }) {
            const user = await service.removeAvatar()
            commit("setCurrentUser", user)
            dispatch(
                "pushNotification",
                {
                    title: ucfirst(i18n.t("common.profilePicWasDeleted")),
                },
                { root: true }
            )
        },

        async fetchEditableFields({ commit }) {
            const editableProfileFields = await service.getEditableFields()
            commit("setEditableFields", editableProfileFields)
        },
    }
}

export default {
    namespaced: true,
    state: makeDefaultState(),
    getters,
    mutations,
    actions: makeActions(DEFAULT_I18N.global, new ProfileService()),
}
